import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classes from 'classnames';
import styles from './PageHeader.module.scss';
import {
  BimloaFilter,
  FilterField,
  FilterValue,
  getEmptyValue,
  getFilterTagText,
  getFilterType,
  isEmptyValue,
} from '@/components/school-management/BimloaFilter/BimloaFilter';
import type { CascadingFilterRequest } from '@/school-management/types/response/autocomplete';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { AnchorRefProps, createAnchorWrapper } from '@/components/school-management/Anchor/Anchor';
import {
  AutocompleteItem,
  getDisplayText,
} from '@/components/school-management/BimloaFilter/AutocompleteInput';

export interface PageHeaderFiltersBarProps {
  filterFields: FilterField[];
  filters: Record<string, FilterValue>;
  setFilters: (filters: Record<string, FilterValue>) => void;
  onRemoveFilterLabel?: (key: string) => void;
  organizationId?: string;
  cascadingFilters?: CascadingFilterRequest;
  disabled?: boolean;
  anchorFilterOverlay?: boolean;
  anchorFilterOverlayPosition?: 'start' | 'end';
  anchorFilterOverlayVerticalOffset?: number;
  anchorFilterOverlayHorizontalOffset?: number;
  className?: string;
  filterAnchorStayInView?: boolean;
}

const ButtonAnchor = createAnchorWrapper('button');
const SCROLL_EDGE_SAFE_ZONE_PX = 18;
const GROUPED_FILTERS_PILL_KEY = '__grouped_active_filters__';

const readCssPx = (value: string | undefined, fallback: number) => {
  if (!value || value === 'normal') return fallback;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const SINGLE_LINE_VIEWPORT_FUDGE_PX = 6;

type FilterMenuItem = {
  id: string;
  label: string;
  onRemove: () => void;
};

type ActiveFilterPill = {
  key: string;
  summary: string;
  menuItems: FilterMenuItem[];
};

const getAutocompleteItemId = (item: AutocompleteItem) => {
  if (typeof item === 'string') {
    return item;
  }

  if ('groupId' in item) {
    return item.groupId;
  }

  return item.id;
};

/**
 * Active filters are rendered as the top-row pills from the Figma design.
 */
const PageHeaderFiltersBar: React.FC<PageHeaderFiltersBarProps> = ({
  filterFields,
  filters,
  setFilters,
  onRemoveFilterLabel,
  organizationId,
  cascadingFilters,
  disabled = false,
  anchorFilterOverlay = true,
  anchorFilterOverlayPosition = 'end',
  anchorFilterOverlayVerticalOffset,
  anchorFilterOverlayHorizontalOffset = 25,
  className,
  filterAnchorStayInView = true,
}) => {
  const { t } = useAdminTranslation();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [hasScrollLeft, setHasScrollLeft] = useState(false);
  const [hasScrollRight, setHasScrollRight] = useState(false);
  const [useInlineFilterLayout, setUseInlineFilterLayout] = useState(true);
  const [openGroupedSections, setOpenGroupedSections] = useState<Record<string, boolean>>({});
  const anchorRefs = useRef<Record<string, AnchorRefProps | null>>({});
  const scrollTrackRef = useRef<HTMLDivElement | null>(null);
  const filtersBarRef = useRef<HTMLDivElement | null>(null);
  const selectedFiltersViewportRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const bimloaWrapRef = useRef<HTMLDivElement | null>(null);
  const groupedAnchorRef = useRef<AnchorRefProps | null>(null);
  const groupedMenuMeasureRef = useRef<HTMLDivElement | null>(null);
  const [groupedPopoverWidth, setGroupedPopoverWidth] = useState<number | undefined>();

  const handleRemoveFilter = useCallback(
    (field: FilterField, nextValue: FilterValue) => {
      if (onRemoveFilterLabel && isEmptyValue(field, nextValue)) {
        onRemoveFilterLabel(field.key);
      }

      setFilters({
        ...filters,
        [field.key]: nextValue,
      });
    },
    [filters, onRemoveFilterLabel, setFilters]
  );

  const activeFilters = useMemo<ActiveFilterPill[]>(() => {
    return filterFields
      .filter(field => !isEmptyValue(field, filters[field.key] as unknown))
      .map(field => {
        const value = filters[field.key];
        const filterType = getFilterType(field);

        const summary = (() => {
          if (
            (filterType === 'MULTI_SELECT' || filterType === 'AUTOCOMPLETE_MULTI_SELECT') &&
            Array.isArray(value)
          ) {
            return `${field.label}: ${value.length}`;
          }

          return field.label;
        })();

        const menuItems = (() => {
          switch (filterType) {
            case 'MULTI_SELECT': {
              const selectedValues = (value as string[]) || [];

              return selectedValues.map(selectedValue => ({
                id: selectedValue,
                label: field.optionLabels?.[selectedValue] ?? selectedValue,
                onRemove: () => {
                  const nextValue = selectedValues.filter(item => item !== selectedValue);
                  handleRemoveFilter(field, nextValue);
                },
              }));
            }

            case 'AUTOCOMPLETE_MULTI_SELECT': {
              const selectedItems = (value as AutocompleteItem[]) || [];

              return selectedItems.map(item => {
                const itemId = getAutocompleteItemId(item);

                return {
                  id: itemId,
                  label: field.autocompleteType ? getDisplayText(item, field.autocompleteType) : '',
                  onRemove: () => {
                    const nextValue = selectedItems.filter(
                      selectedItem => getAutocompleteItemId(selectedItem) !== itemId
                    );
                    handleRemoveFilter(field, nextValue);
                  },
                };
              });
            }

            case 'AUTOCOMPLETE': {
              const selectedItem = value as AutocompleteItem | null;

              if (!selectedItem || !field.autocompleteType) {
                return [];
              }

              return [
                {
                  id: field.key,
                  label: getDisplayText(selectedItem, field.autocompleteType),
                  onRemove: () => handleRemoveFilter(field, getEmptyValue(field)),
                },
              ];
            }

            default: {
              const label = getFilterTagText(field, value, t);

              if (!label) {
                return [];
              }

              return [
                {
                  id: field.key,
                  label,
                  onRemove: () => handleRemoveFilter(field, getEmptyValue(field)),
                },
              ];
            }
          }
        })();

        return {
          key: field.key,
          summary,
          menuItems,
        };
      })
      .filter(filter => filter.menuItems.length > 0);
  }, [filterFields, filters, handleRemoveFilter, t]);

  const closeAllAnchorsExcept = useCallback((keepKey: string | null) => {
    Object.entries(anchorRefs.current).forEach(([key, anchorRef]) => {
      if (key !== keepKey) {
        anchorRef?.close();
      }
    });
    if (keepKey !== GROUPED_FILTERS_PILL_KEY) {
      groupedAnchorRef.current?.close();
    }
  }, []);

  const updateInlineLayoutFits = useCallback(() => {
    const viewport = selectedFiltersViewportRef.current;
    const measure = measureRef.current;
    const root = filtersBarRef.current;
    const bimloaWrap = bimloaWrapRef.current;

    if (activeFilters.length === 0) {
      setUseInlineFilterLayout(true);
      return;
    }
    if (!viewport || !measure || !root) {
      return;
    }
    if (viewport.clientWidth === 0) {
      return;
    }

    const pillsWidth = measure.scrollWidth;
    let availableWidth = viewport.clientWidth;

    const toolsRow = root.parentElement as HTMLElement | null | undefined;
    const headerRow = toolsRow?.parentElement as HTMLElement | null | undefined;
    const titleColumn = headerRow?.firstElementChild as HTMLElement | null | undefined;

    if (toolsRow && headerRow && titleColumn) {
      const toolsTop = toolsRow.getBoundingClientRect().top;
      const titleTop = titleColumn.getBoundingClientRect().top;
      const sameLineAsTitle = Math.abs(toolsTop - titleTop) < 8;

      if (!sameLineAsTitle) {
        const headerCs = getComputedStyle(headerRow);
        const headerColumnGap = readCssPx(headerCs.columnGap, readCssPx(headerCs.gap, 24));

        const toolsCs = getComputedStyle(toolsRow);
        const toolsGap = readCssPx(toolsCs.columnGap, readCssPx(toolsCs.gap, 17));

        const actionsEl = root.nextElementSibling as HTMLElement | undefined;
        const actionsWidth = actionsEl?.offsetWidth ?? 0;
        const bimloaWidth = bimloaWrap?.offsetWidth ?? 0;

        const headerInnerWidth = headerRow.clientWidth;
        const titleBlockWidth = titleColumn.getBoundingClientRect().width;

        const singleLineToolsRowBudget = Math.max(
          0,
          headerInnerWidth - titleBlockWidth - headerColumnGap
        );
        const singleLineFiltersBarBudget = Math.max(
          0,
          singleLineToolsRowBudget - actionsWidth - (actionsEl ? toolsGap : 0)
        );
        const singleLineViewportBudget = Math.max(
          0,
          singleLineFiltersBarBudget - bimloaWidth - SINGLE_LINE_VIEWPORT_FUDGE_PX
        );

        availableWidth = Math.min(availableWidth, singleLineViewportBudget);
      }
    }

    const tolerancePx = 1;
    setUseInlineFilterLayout(pillsWidth <= availableWidth + tolerancePx);
  }, [activeFilters]);

  useLayoutEffect(() => {
    updateInlineLayoutFits();
  }, [updateInlineLayoutFits]);

  useEffect(() => {
    const root = filtersBarRef.current;
    const headerRow = root?.parentElement?.parentElement;
    if (!root || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateInlineLayoutFits);
      return () => window.removeEventListener('resize', updateInlineLayoutFits);
    }
    const observer = new ResizeObserver(() => updateInlineLayoutFits());
    observer.observe(root);
    if (headerRow instanceof HTMLElement) {
      observer.observe(headerRow);
    }
    window.addEventListener('resize', updateInlineLayoutFits);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateInlineLayoutFits);
    };
  }, [updateInlineLayoutFits]);

  useEffect(() => {
    const activeKeys = new Set(activeFilters.map(filter => filter.key));
    if (useInlineFilterLayout) {
      delete anchorRefs.current[GROUPED_FILTERS_PILL_KEY];
      Object.keys(anchorRefs.current).forEach(key => {
        if (!activeKeys.has(key)) {
          delete anchorRefs.current[key];
        }
      });
    } else {
      Object.keys(anchorRefs.current).forEach(key => {
        delete anchorRefs.current[key];
      });
    }

    if (expandedKey === GROUPED_FILTERS_PILL_KEY && activeFilters.length === 0) {
      setExpandedKey(null);
    } else if (
      expandedKey &&
      expandedKey !== GROUPED_FILTERS_PILL_KEY &&
      !activeKeys.has(expandedKey)
    ) {
      setExpandedKey(null);
    }
  }, [activeFilters, expandedKey, useInlineFilterLayout]);

  useEffect(() => {
    if (expandedKey !== GROUPED_FILTERS_PILL_KEY) return;
    setOpenGroupedSections(prev => {
      const next = { ...prev };
      activeFilters.forEach(filter => {
        if (next[filter.key] === undefined) {
          next[filter.key] = true;
        }
      });
      Object.keys(next).forEach(key => {
        if (!activeFilters.some(f => f.key === key)) {
          delete next[key];
        }
      });
      return next;
    });
  }, [expandedKey, activeFilters]);

  useLayoutEffect(() => {
    if (expandedKey !== GROUPED_FILTERS_PILL_KEY) {
      setGroupedPopoverWidth(undefined);
      return;
    }

    const menu = groupedMenuMeasureRef.current;
    const trigger = groupedAnchorRef.current?.target as HTMLElement | undefined;
    if (!menu || !trigger) {
      return;
    }

    const cap = Math.min(480, Math.max(0, window.innerWidth - 32));
    const tw = Math.ceil(trigger.getBoundingClientRect().width);
    const intrinsic = Math.ceil(Math.max(menu.scrollWidth, menu.getBoundingClientRect().width));
    const next = Math.min(Math.max(tw, intrinsic), cap);

    setGroupedPopoverWidth(prev => (prev === next ? prev : next));
  }, [expandedKey, activeFilters, openGroupedSections]);

  useEffect(() => {
    if (expandedKey !== GROUPED_FILTERS_PILL_KEY) {
      return;
    }

    const handleResize = () => {
      const menu = groupedMenuMeasureRef.current;
      const trigger = groupedAnchorRef.current?.target as HTMLElement | undefined;
      if (!menu || !trigger) {
        return;
      }

      const cap = Math.min(480, Math.max(0, window.innerWidth - 32));
      const tw = Math.ceil(trigger.getBoundingClientRect().width);
      const intrinsic = Math.ceil(Math.max(menu.scrollWidth, menu.getBoundingClientRect().width));
      const next = Math.min(Math.max(tw, intrinsic), cap);

      setGroupedPopoverWidth(next);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [expandedKey, activeFilters, openGroupedSections]);

  useEffect(() => {
    if (useInlineFilterLayout) {
      setGroupedPopoverWidth(undefined);
    }
  }, [useInlineFilterLayout]);

  useEffect(() => {
    setExpandedKey(null);
    closeAllAnchorsExcept(null);
  }, [useInlineFilterLayout, closeAllAnchorsExcept]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-filter-overlay="true"]')) return;
      closeAllAnchorsExcept(null);
      setExpandedKey(null);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [closeAllAnchorsExcept]);

  useEffect(() => {
    const track = scrollTrackRef.current;
    if (!track) return;

    const updateScrollIndicators = () => {
      const maxLeft = Math.max(track.scrollWidth - track.clientWidth, 0);
      setHasScrollLeft(track.scrollLeft > 2);
      setHasScrollRight(track.scrollLeft < maxLeft - 2);
    };

    updateScrollIndicators();
    track.addEventListener('scroll', updateScrollIndicators, { passive: true });
    window.addEventListener('resize', updateScrollIndicators);

    return () => {
      track.removeEventListener('scroll', updateScrollIndicators);
      window.removeEventListener('resize', updateScrollIndicators);
    };
  }, [activeFilters, useInlineFilterLayout]);

  const isElementFullyVisibleInTrack = (element: HTMLElement, track: HTMLDivElement) => {
    const elementRect = element.getBoundingClientRect();
    const trackRect = track.getBoundingClientRect();
    const visibleLeft = trackRect.left + (hasScrollLeft ? SCROLL_EDGE_SAFE_ZONE_PX : 0);
    const visibleRight = trackRect.right - (hasScrollRight ? SCROLL_EDGE_SAFE_ZONE_PX : 0);
    return elementRect.left >= visibleLeft && elementRect.right <= visibleRight;
  };

  useEffect(() => {
    const track = scrollTrackRef.current;
    if (!track) return;

    const handleTrackScroll = () => {
      if (!expandedKey) return;
      closeAllAnchorsExcept(null);
      setExpandedKey(null);
    };

    track.addEventListener('scroll', handleTrackScroll, { passive: true });
    return () => track.removeEventListener('scroll', handleTrackScroll);
  }, [expandedKey, closeAllAnchorsExcept]);

  const toggleAnchor = useCallback(
    (key: string) => {
      if (key === GROUPED_FILTERS_PILL_KEY) {
        const anchor = groupedAnchorRef.current;
        if (!anchor) return;
        const currentlyOpen = anchor.isOpen();
        if (currentlyOpen) {
          anchor.close();
          setExpandedKey(null);
          return;
        }
        closeAllAnchorsExcept(GROUPED_FILTERS_PILL_KEY);
        const triggerEl = anchor.target as HTMLElement | undefined;
        const rect = triggerEl?.getBoundingClientRect();
        if (rect && window.innerHeight - rect.bottom < 120) {
          return;
        }
        anchor.open();
        setExpandedKey(GROUPED_FILTERS_PILL_KEY);
        return;
      }

      const anchor = anchorRefs.current[key];
      if (!anchor) return;
      const track = scrollTrackRef.current;
      const filterElement = track?.querySelector(`[data-filter-key="${key}"]`);

      const currentlyOpen = anchor.isOpen();
      if (currentlyOpen) {
        anchor.close();
        setExpandedKey(null);
        return;
      }

      if (track && filterElement instanceof HTMLElement) {
        if (!isElementFullyVisibleInTrack(filterElement, track)) {
          filterElement.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
          window.setTimeout(() => {
            if (!scrollTrackRef.current) return;
            if (!isElementFullyVisibleInTrack(filterElement, scrollTrackRef.current)) return;
            const rect = filterElement.getBoundingClientRect();
            const minMenuSpace = 120;
            if (window.innerHeight - rect.bottom < minMenuSpace) return;
            closeAllAnchorsExcept(key);
            anchor.open();
            setExpandedKey(key);
          }, 180);
          return;
        }

        const rect = filterElement.getBoundingClientRect();
        const minMenuSpace = 120;
        if (window.innerHeight - rect.bottom < minMenuSpace) {
          return;
        }
      }

      closeAllAnchorsExcept(key);
      anchor.open();
      setExpandedKey(key);
    },
    [closeAllAnchorsExcept, hasScrollLeft, hasScrollRight]
  );

  const toggleGroupedSection = useCallback((filterKey: string) => {
    setOpenGroupedSections(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey],
    }));
  }, []);

  const renderGroupedFiltersMenu = () => (
    <div
      ref={groupedMenuMeasureRef}
      className={classes(styles.hiddenFiltersMenu, styles.hiddenFiltersMenuGrouped)}
      data-filter-overlay='true'
      style={{
        boxSizing: 'border-box',
        width: groupedPopoverWidth !== undefined ? '100%' : 'max-content',
        maxWidth: 'min(480px, calc(100vw - 32px))',
      }}
    >
      {activeFilters.map(filter => {
        const sectionOpen = openGroupedSections[filter.key] ?? true;

        return (
          <div key={filter.key} className={styles.hiddenFilterSection} data-filter-overlay='true'>
            <button
              type='button'
              className={styles.hiddenFilterSectionToggle}
              onClick={() => toggleGroupedSection(filter.key)}
              disabled={disabled}
              data-filter-overlay='true'
            >
              <span className={styles.hiddenFilterSectionToggleLabel}>{filter.summary}</span>
              <AppIcon
                icon='dropDown'
                width={12.8}
                height={12.8}
                className={classes(styles.hiddenFilterSectionChevron, {
                  [styles.hiddenFilterSectionChevronOpen]: sectionOpen,
                })}
              />
            </button>
            {sectionOpen && (
              <div className={styles.groupedSectionBody} data-filter-overlay='true'>
                {filter.menuItems.map(item => (
                  <div
                    key={item.id}
                    className={classes(styles.filterMenuItem, {
                      [styles.filterMenuItemDisabled]: disabled,
                    })}
                  >
                    <span className={styles.filterMenuItemText}>{item.label}</span>
                    <button
                      type='button'
                      className={styles.filterMenuRemove}
                      aria-label={`Remove ${item.label}`}
                      onClick={item.onRemove}
                    >
                      <AppIcon icon='x' width={16} height={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderFilterMenu = (filter: ActiveFilterPill) => (
    <div
      className={classes(styles.filterMenu, {
        [styles.filterMenuDisabled]: disabled,
      })}
      data-filter-overlay='true'
    >
      {filter.menuItems.map(item => (
        <div
          key={item.id}
          className={classes(styles.filterMenuItem, {
            [styles.filterMenuItemDisabled]: disabled,
          })}
        >
          <span className={styles.filterMenuItemText}>{item.label}</span>
          <button
            type='button'
            className={styles.filterMenuRemove}
            aria-label={`Remove ${item.label}`}
            onClick={item.onRemove}
          >
            <AppIcon icon='x' width={16} height={16} />
          </button>
        </div>
      ))}
    </div>
  );

  const handleTrackWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const track = scrollTrackRef.current;
    if (!track) return;
    if (track.scrollWidth <= track.clientWidth + 1) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    event.preventDefault();
    if (expandedKey) {
      closeAllAnchorsExcept(null);
      setExpandedKey(null);
    }
    track.scrollLeft += event.deltaY;
  };

  const groupedPillLabel = t('pageHeader.groupedActiveFiltersSummary', {
    count: activeFilters.length,
    defaultValue: `Filters: ${activeFilters.length}`,
  });

  return (
    <div ref={filtersBarRef} className={`${styles.filtersBar}${className ? ` ${className}` : ''}`}>
      <div ref={measureRef} className={styles.filtersInlineMeasure} aria-hidden>
        {activeFilters.map(filter => (
          <span key={filter.key} className={styles.filterPillButton}>
            <span className={styles.pillContent}>
              <span className={styles.pillText}>{filter.summary}</span>
              <AppIcon icon='dropDown' width={12.8} height={12.8} className={styles.pillChevron} />
            </span>
          </span>
        ))}
      </div>

      <div
        ref={selectedFiltersViewportRef}
        className={`${styles.selectedFiltersViewport} ${hasScrollLeft ? styles.scrollLeft : ''} ${
          hasScrollRight ? styles.scrollRight : ''
        }`}
      >
        <div
          ref={scrollTrackRef}
          className={classes(styles.selectedFiltersTrack, {
            [styles.selectedFiltersTrackGrouped]: !useInlineFilterLayout,
          })}
          onWheel={handleTrackWheel}
        >
          {!useInlineFilterLayout && activeFilters.length > 0 ? (
            <ButtonAnchor
              ref={instance => {
                groupedAnchorRef.current = instance;
              }}
              type='button'
              data-filter-key={GROUPED_FILTERS_PILL_KEY}
              data-filter-overlay='true'
              className={classes(styles.filterPillButton, styles.overflowPillButton, {
                [styles.filterPillButtonOpen]: expandedKey === GROUPED_FILTERS_PILL_KEY,
                [styles.filterPillButtonDisabled]: disabled,
              })}
              style={
                expandedKey === GROUPED_FILTERS_PILL_KEY && groupedPopoverWidth !== undefined
                  ? {
                      width: groupedPopoverWidth,
                      boxSizing: 'border-box',
                    }
                  : undefined
              }
              onMouseDown={event => event.stopPropagation()}
              onTouchStart={event => event.stopPropagation()}
              onClick={() => toggleAnchor(GROUPED_FILTERS_PILL_KEY)}
              trigerType='none'
              autoClose={true}
              stayInView={filterAnchorStayInView}
              tooltip={renderGroupedFiltersMenu()}
              renderTooltipPosition={anchorPosition => ({
                top: anchorPosition.top + anchorPosition.height,
                insetInlineEnd: anchorPosition.right,
                ...(groupedPopoverWidth !== undefined ? { width: groupedPopoverWidth } : {}),
              })}
            >
              <span className={styles.pillContent}>
                <span className={styles.pillText}>{groupedPillLabel}</span>
                <AppIcon
                  icon='dropDown'
                  width={12.8}
                  height={12.8}
                  className={styles.pillChevron}
                />
              </span>
            </ButtonAnchor>
          ) : (
            activeFilters.map(filter => (
              <ButtonAnchor
                key={filter.key}
                ref={instance => {
                  anchorRefs.current[filter.key] = instance;
                }}
                type='button'
                data-filter-key={filter.key}
                data-filter-overlay='true'
                className={classes(styles.filterPillButton, {
                  [styles.filterPillButtonOpen]: expandedKey === filter.key,
                  [styles.filterPillButtonDisabled]: disabled,
                })}
                onMouseDown={event => event.stopPropagation()}
                onTouchStart={event => event.stopPropagation()}
                onClick={() => toggleAnchor(filter.key)}
                trigerType='none'
                autoClose={true}
                stayInView={filterAnchorStayInView}
                tooltip={renderFilterMenu(filter)}
                renderTooltipPosition={anchorPosition => ({
                  top: anchorPosition.top + anchorPosition.height,
                  left: anchorPosition.left,
                  width: anchorPosition.width,
                })}
              >
                <span className={styles.pillContent}>
                  <span className={styles.pillText}>{filter.summary}</span>
                  <AppIcon
                    icon='dropDown'
                    width={12.8}
                    height={12.8}
                    className={styles.pillChevron}
                  />
                </span>
              </ButtonAnchor>
            ))
          )}
        </div>
      </div>

      <div ref={bimloaWrapRef} className={styles.filtersBarBimloaWrap}>
        <BimloaFilter
          fields={filterFields}
          values={filters}
          onChange={setFilters}
          anchorFilterOverlay={anchorFilterOverlay}
          anchorFilterOverlayPosition={anchorFilterOverlayPosition}
          anchorFilterOverlayVerticalOffset={anchorFilterOverlayVerticalOffset}
          anchorFilterOverlayHorizontalOffset={anchorFilterOverlayHorizontalOffset}
          organizationId={organizationId}
          disabled={disabled}
          cascadingFilters={cascadingFilters}
          anchorStayInView={filterAnchorStayInView}
        />
      </div>
    </div>
  );
};

export default PageHeaderFiltersBar;
